import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faGlobe, faChevronDown, faCheck,
    faSearch, faTimes, faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const OrgSwitcher = () => {
    const { isSuper, currentUser, activeOrgId, activeOrgName, setActiveOrg } = useAuth();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch organizations list for Superadmin
    useEffect(() => {
        if (!isSuper) return;
        let isMounted = true;
        const fetchOrgs = async () => {
            try {
                setLoading(true);
                const res = await api.get('/organizations');
                if (isMounted) {
                    setOrganizations(res.data || []);
                }
            } catch (err) {
                console.error('Failed to load organizations for switcher:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchOrgs();

        // Listen for new/updated organizations to refresh
        const handleRefresh = () => fetchOrgs();
        window.addEventListener('sv_organizations_updated', handleRefresh);
        return () => {
            isMounted = false;
            window.removeEventListener('sv_organizations_updated', handleRefresh);
        };
    }, [isSuper]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // For non-superadmin: Show compact organization badge
    if (!isSuper) {
        return (
            <div className="org-badge-non-super" title={`Your Organization: ${currentUser?.OrgName || 'SalesVault'}`}>
                <div className="org-btn-icon-wrapper">
                    <FontAwesomeIcon icon={faBuilding} className="org-badge-icon" />
                </div>
                <span className="org-badge-name">{currentUser?.OrgName || 'CodexterLabs'}</span>
            </div>
        );
    }

    const filteredOrgs = organizations.filter(o =>
        o.OrgName.toLowerCase().includes(search.toLowerCase()) ||
        (o.OrgCode && o.OrgCode.toLowerCase().includes(search.toLowerCase()))
    );

    const isAll = activeOrgId === 'all';

    const handleSelect = (orgId, orgName) => {
        setActiveOrg(orgId, orgName);
        setOpen(false);
        setSearch('');
    };

    return (
        <div className="org-switcher-wrapper" ref={dropdownRef}>
            <button
                type="button"
                className={`org-switcher-btn ${open ? 'active' : ''} ${isAll ? 'all-mode' : ''}`}
                onClick={() => setOpen(!open)}
                title="Switch active organization scope"
                aria-expanded={open}
            >
                <div className="org-btn-icon-wrapper">
                    <FontAwesomeIcon icon={isAll ? faGlobe : faBuilding} className="org-btn-icon" />
                </div>
                <span className="org-btn-name">{activeOrgName}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`org-btn-caret ${open ? 'rotated' : ''}`} />
            </button>

            {open && (
                <div className="org-switcher-dropdown">
                    <div className="org-dropdown-header">
                        <div className="org-search-box">
                            <FontAwesomeIcon icon={faSearch} className="org-search-icon" />
                            <input
                                type="text"
                                placeholder="Search organizations..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                autoFocus
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="org-search-clear"
                                    onClick={() => setSearch('')}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="org-list">
                        {/* Option: All Organizations */}
                        <div
                            className={`org-list-item all-option ${isAll ? 'selected' : ''}`}
                            onClick={() => handleSelect('all', 'All Organizations')}
                        >
                            <div className="org-item-icon all">
                                <FontAwesomeIcon icon={faGlobe} />
                            </div>
                            <div className="org-item-info">
                                <div className="org-item-title-row">
                                    <span className="org-item-name">All Organizations</span>
                                </div>
                            </div>
                            {isAll && <FontAwesomeIcon icon={faCheck} className="org-item-check" />}
                        </div>

                        <div className="org-list-divider" />
                        <div className="org-section-header">Organizations ({organizations.length})</div>

                        {loading && (
                            <div className="org-loading-row">Loading organizations...</div>
                        )}

                        {!loading && filteredOrgs.length === 0 && (
                            <div className="org-empty-row">No organizations found</div>
                        )}

                        {filteredOrgs.map(org => {
                            const isSelected = String(org.OrgID) === String(activeOrgId);
                            return (
                                <div
                                    key={org.OrgID}
                                    className={`org-list-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleSelect(org.OrgID, org.OrgName)}
                                >
                                    <div className="org-item-icon">
                                        <FontAwesomeIcon icon={faBuilding} />
                                    </div>
                                    <div className="org-item-info">
                                        <div className="org-item-title-row">
                                            <span className="org-item-name">{org.OrgName}</span>
                                            {org.OrgCode && <span className="org-code-pill">{org.OrgCode}</span>}
                                        </div>
                                        <div className="org-item-meta">
                                            <span className={`org-status-dot ${org.Status === 'Active' ? 'active' : 'suspended'}`} />
                                            <span>{org.Status}</span>
                                            {org.TotalUsers !== undefined && (
                                                <span className="org-meta-dot">• {org.TotalUsers} users</span>
                                            )}
                                        </div>
                                    </div>
                                    {isSelected && <FontAwesomeIcon icon={faCheck} className="org-item-check" />}
                                </div>
                            );
                        })}
                    </div>

                    <div className="org-dropdown-footer">
                        <button
                            type="button"
                            className="manage-orgs-btn"
                            onClick={() => {
                                setOpen(false);
                                navigate('/organizations');
                            }}
                        >
                            <FontAwesomeIcon icon={faLayerGroup} className="manage-orgs-icon" />
                            <span>Manage Organizations</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgSwitcher;
